import { type MedplumClient, resolveId } from "@medplum/core";
import type {
  Account,
  ChargeItem,
  HealthcareService,
  Organization,
  Patient,
  Reference,
} from "@medplum/fhirtypes";
import {
  FhirResource,
  type SearchOptions,
  type SubscriptionCallback,
} from "@vibrant/models/fhir/FhirResource";
import type { ResourceData, ResourceRef, ValueOf } from "@vibrant/models/fhir/fhir-resource-types";
import { time } from "@/lib/time";
import { VIBRANT_IDENTIFIER_SYSTEM_SERVICES } from "@/models/practice/services/constants";
import { getService } from "@/models/practice/services/Service";
import { MemberAccount } from "./MemberAccount";

export interface PurchaseCreateRequest {
  account: ResourceRef;
  service?: ResourceRef;
  /** A Stripe price charged directly, with no `HealthcareService` behind it. */
  stripePrice?: {
    id: string;
    name: string;
  };
  hintChargeItem?: {
    id: string;
    name: string;
  };
  hintMembership?: {
    id: string;
    name: string;
  };
  status: ChargeItem["status"];
  quantity?: number;
  dueDate?: string;
  priceInCents?: number;
  hintChargeId?: string;
  hintInvoiceId?: string;
  hintSyncStatus?: PurchaseHintSyncStatus;
  hintChargeCreatedAt?: string;
  identifier?: Array<{
    system: string;
    value: string;
  }>;
}

export interface PurchaseCreateOptions {
  ifNoneExist?: string;
}

export type PurchaseUpdateRequest = Partial<
  Omit<PurchaseCreateRequest, "account" | "service" | "hintChargeItem" | "hintMembership">
> & {
  hintChargeId?: string;
  hintInvoiceId?: string;
  hintSyncStatus?: ValueOf<typeof Purchase.HintSyncStatus>;
  hintChargeCreatedAt?: string;
  hintMembershipId?: string;
  hintMembershipPlan?: string;
  hintMembershipPlanType?: string;
  hintMembershipPayer?: string;
  hintMembershipCompany?: string;
  hintMembershipStartDate?: string;
  /** `null` removes a stored end date (Hint withdrew a scheduled end). */
  hintMembershipEndDate?: string | null;
  hintMembershipStatus?: string;
  hintMembershipPeriodInMonths?: number;
  hintMembershipRateInCents?: number;
  hintMembershipIsRecurring?: boolean;
  priceInCents?: number;
};

type PurchaseData = PurchaseCreateRequest & {
  practice: ResourceRef;
  member: ResourceRef;
};

export interface PurchaseSearchQuery {
  account?: ResourceRef;
  service?: ResourceRef;
  stripePriceId?: string;
  hintChargeItem?: {
    id: string;
  };
  hintMembershipId?: string;
  isHintCharge?: boolean;
  isHintMembership?: boolean;
}

export type PurchaseStatus = ValueOf<typeof Purchase.Status>;

export type PurchaseHintSyncStatus = ValueOf<typeof Purchase.HintSyncStatus>;

export function formatHintMembershipPlanType(planType: string | undefined): string | undefined {
  const normalizedPlanType = planType?.trim().replace(/[_-]+/g, " ");
  if (!normalizedPlanType) {
    return undefined;
  }

  return normalizedPlanType
    .split(/\s+/)
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`)
    .join(" ");
}

const UNKNOWN_HINT_MEMBERSHIP_PLAN = "Unknown Plan";

/**
 * Purchase (FHIR: ChargeItem)
 *
 * Encapsulates a purchase (or pending purchase) of a given `Service`, `HintChargeItem`, or `HintMembership` under a `MemberAccount`.
 * Supports:
 * - Services coding system (vibrant.services)
 * - StripePrices coding system (vibrant.stripe.prices) — a bare price, no HealthcareService
 * - HintChargeItems coding system (vibrant.hint.charge-items)
 * - HintMemberships coding system (vibrant.hint.memberships)
 */
export class Purchase extends FhirResource<ChargeItem> {
  static readonly ResourceType = "ChargeItem" as const;

  static readonly Coding = {
    System: {
      Services: VIBRANT_IDENTIFIER_SYSTEM_SERVICES,
      HintChargeItems: "vibrant.hint.charge-items",
      HintMemberships: "vibrant.hint.memberships",
      /**
       * A bare Stripe price, with no `HealthcareService` behind it.
       *
       * A workflow payment step charges a price straight off the practice's connected account —
       * practice Stripe prices are not all mirrored as `HealthcareService`, which is why the step
       * binds `stripe-price/<id>` rather than a service. Without this system there was no way to
       * record that the patient paid, so the step could never complete.
       */
      StripePrices: "vibrant.stripe.prices",
    },
  };

  static readonly Identifier = {
    BookingChargeKey: "vibrant.purchase.bookingChargeKey",
    /** The appointment this purchase was charged for — lets a cancellation/no-show handler find
     * every Hint charge tied to a visit without reconstructing `bookingChargeKey`. */
    AppointmentId: "vibrant.purchase.appointmentId",
  } as const;

  static readonly Status = {
    Paid: "billed",
    Unpaid: "billable",
    NotBillable: "not-billable",
  } as const;

  static readonly HintSyncStatus = {
    Pending: "pending",
    Synced: "synced",
    Failed: "failed",
  } as const;

  static readonly Extension = {
    PriceInCents: "vibrant.purchase.priceInCents",
    HintChargeId: "vibrant.purchase.hintChargeId",
    HintInvoiceId: "vibrant.purchase.hintInvoiceId",
    HintSyncStatus: "vibrant.purchase.hintSyncStatus",
    HintChargeCreatedAt: "vibrant.purchase.hintChargeCreatedAt",
    HintMembershipId: "vibrant.purchase.hintMembershipId",
    HintMembershipPlan: "vibrant.purchase.hintMembershipPlan",
    HintMembershipPlanType: "vibrant.purchase.hintMembershipPlanType",
    HintMembershipPayer: "vibrant.purchase.hintMembershipPayer",
    HintMembershipCompany: "vibrant.purchase.hintMembershipCompany",
    HintMembershipStartDate: "vibrant.purchase.hintMembershipStartDate",
    HintMembershipEndDate: "vibrant.purchase.hintMembershipEndDate",
    HintMembershipStatus: "vibrant.purchase.hintMembershipStatus",
    HintMembershipPeriodInMonths: "vibrant.purchase.hintMembershipPeriodInMonths",
    HintMembershipRateInCents: "vibrant.purchase.hintMembershipRateInCents",
    HintMembershipIsRecurring: "vibrant.purchase.hintMembershipIsRecurring",
  } as const;

  //----------------------------------------------------------------------------------------------
  //                                             GETTERS
  //----------------------------------------------------------------------------------------------

  /**
   * Returns the derived high-level status from the FHIR ChargeItem.status.
   * Mapping:
   * - billed -> Paid
   * - billable -> Unpaid
   * - not-billable -> NotBillable
   */
  get status(): ChargeItem["status"] {
    return this.resource.status;
  }

  /** Returns true if the purchase is paid. */
  get isPaid(): boolean {
    return this.status === Purchase.Status.Paid;
  }

  /** Returns true if the purchase is pending. */
  get isPending(): boolean {
    return this.status === Purchase.Status.Unpaid;
  }

  /** Returns true if the purchase can still be billed. */
  get isBillable(): boolean {
    return this.status === Purchase.Status.Unpaid;
  }

  /** Returns the due date of the purchase. */
  get dueDate(): string | undefined {
    return this.resource.occurrenceDateTime;
  }

  /** Returns true if the purchase is overdue. */
  get isOverdue(): boolean {
    return this.status === Purchase.Status.Unpaid && time(this.dueDate).isBefore(time());
  }

  /**
   * Returns when the underlying ChargeItem was last modified — used as a proxy for "when this
   * purchase became Paid" since status transitions (Unpaid -> Paid) go through a patch write.
   */
  get lastUpdated(): string | undefined {
    return this.resource.meta?.lastUpdated;
  }

  /** Returns the member (patient) reference. */
  get memberRef(): Reference<Patient> | undefined {
    return this.resource.subject as Reference<Patient> | undefined;
  }

  /** Returns the member (patient) ID. */
  get memberId(): string | undefined {
    const ref = this.memberRef;
    return ref ? (resolveId(ref) ?? undefined) : undefined;
  }

  /** Returns the linked account reference (first). */
  get accountRef(): Reference<Account> | undefined {
    const accountRefs = this.resource.account as Reference<Account>[] | undefined;
    return accountRefs && accountRefs.length > 0 ? accountRefs[0] : undefined;
  }

  /** Returns the linked account ID. */
  get accountId(): string | undefined {
    const ref = this.accountRef;
    return ref ? (resolveId(ref) ?? undefined) : undefined;
  }

  /** Returns the linked service reference (first). */
  get serviceRef(): Reference<HealthcareService> | undefined {
    const serviceCoding = this.resource.code?.coding?.find(
      (coding) => coding.system === Purchase.Coding.System.Services,
    );
    return serviceCoding ? { reference: `HealthcareService/${serviceCoding.code}` } : undefined;
  }

  /** Returns the linked service ID. */
  get serviceId(): string | undefined {
    const ref = this.serviceRef;
    return ref ? (resolveId(ref) ?? undefined) : undefined;
  }

  /** Returns the Hint charge item ID if this purchase uses HintChargeItems coding. */
  /** The Stripe price this purchase is for, when it is a bare price rather than a Service. */
  get stripePriceId(): string | undefined {
    const coding = this.resource.code?.coding?.find(
      (entry) => entry.system === Purchase.Coding.System.StripePrices,
    );
    return coding?.code;
  }

  get hintChargeItemId(): string | undefined {
    const hintChargeItemCoding = this.resource.code?.coding?.find(
      (coding) => coding.system === Purchase.Coding.System.HintChargeItems,
    );
    return hintChargeItemCoding?.code;
  }

  /** Returns the performing organization reference (practice). */
  get practiceRef(): Reference<Organization> | undefined {
    return this.resource.performingOrganization as Reference<Organization> | undefined;
  }

  /** Returns the performing organization (practice) ID. */
  get practiceId(): string | undefined {
    const ref = this.practiceRef;
    return ref ? (resolveId(ref) ?? undefined) : undefined;
  }

  /** Returns the quantity of the purchase. Defaults to 1 if not specified. */
  get quantity(): number {
    return this.resource.quantity?.value ?? 1;
  }

  /** Returns the price of the purchase in dollars. */
  get price(): number {
    const priceInCents = this.getExtension(Purchase.Extension.PriceInCents)?.valueInteger;
    if (typeof priceInCents === "number" && priceInCents >= 0) {
      return priceInCents / 100;
    }
    return 0;
  }

  /** Returns the price of the purchase in cents. */
  get priceInCents(): number {
    const priceInCents = this.getExtension(Purchase.Extension.PriceInCents)?.valueInteger;
    if (typeof priceInCents === "number" && priceInCents >= 0) {
      return priceInCents;
    }
    return 0;
  }

  /** Returns the Hint charge ID if this purchase is synced to Hint. */
  get hintChargeId(): string | undefined {
    return this.getExtension(Purchase.Extension.HintChargeId)?.valueString;
  }

  /** Returns the appointment this purchase was charged for, if any. */
  get appointmentId(): string | undefined {
    const value = this.getIdentifier(Purchase.Identifier.AppointmentId);
    return typeof value === "string" ? value : undefined;
  }

  /** Returns the Hint invoice ID if this purchase is synced to Hint. */
  get hintInvoiceId(): string | undefined {
    return this.getExtension(Purchase.Extension.HintInvoiceId)?.valueString;
  }

  /** Returns the Hint sync status. */
  get hintSyncStatus(): string | undefined {
    return this.getExtension(Purchase.Extension.HintSyncStatus)?.valueString;
  }

  /** Returns the Hint charge item name. */
  get hintChargeItemName(): string | undefined {
    const hintChargeItemCoding = this.resource.code?.coding?.find(
      (coding) => coding.system === Purchase.Coding.System.HintChargeItems,
    );
    return hintChargeItemCoding?.display;
  }

  /** Returns the Hint charge creation timestamp. */
  get hintChargeCreatedAt(): string | undefined {
    return this.getExtension(Purchase.Extension.HintChargeCreatedAt)?.valueDateTime;
  }

  /** Returns true if this purchase is synced to Hint. */
  get isHintSynced(): boolean {
    return this.hintSyncStatus === Purchase.HintSyncStatus.Synced;
  }

  /** Returns true if this purchase sync to Hint failed. */
  get isHintSyncFailed(): boolean {
    return this.hintSyncStatus === Purchase.HintSyncStatus.Failed;
  }

  /** Returns true if this purchase sync to Hint is pending. */
  get isHintSyncPending(): boolean {
    return this.hintSyncStatus === Purchase.HintSyncStatus.Pending;
  }

  /** Returns the Hint membership ID if this purchase is a membership. */
  get hintMembershipId(): string | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipId)?.valueString;
  }

  /** Returns the Hint membership plan name. */
  get hintMembershipPlan(): string | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipPlan)?.valueString;
  }

  /** Returns the Hint membership plan type. */
  get hintMembershipPlanType(): string | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipPlanType)?.valueString;
  }

  /** Returns the user-facing Hint membership label from Hint plan metadata. */
  get hintMembershipPlanLabel(): string | undefined {
    const planName = this.hintMembershipPlan?.trim();
    const planTypeLabel = formatHintMembershipPlanType(this.hintMembershipPlanType);
    if (!planName || planName === UNKNOWN_HINT_MEMBERSHIP_PLAN) {
      return planTypeLabel || planName;
    }

    return planName;
  }

  /** Returns the Hint membership payer name. */
  get hintMembershipPayer(): string | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipPayer)?.valueString;
  }

  /** Returns the Hint membership company name. */
  get hintMembershipCompany(): string | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipCompany)?.valueString;
  }

  /** Returns the Hint membership start date. */
  get hintMembershipStartDate(): string | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipStartDate)?.valueString;
  }

  /** Returns the Hint membership end date. */
  get hintMembershipEndDate(): string | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipEndDate)?.valueString;
  }

  /** Returns the Hint membership status. */
  get hintMembershipStatus(): string | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipStatus)?.valueString;
  }

  /** Returns the Hint membership period in months. */
  get hintMembershipPeriodInMonths(): number | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipPeriodInMonths)?.valueInteger;
  }

  /** Returns the Hint membership rate in cents. */
  get hintMembershipRateInCents(): number | undefined {
    return this.getExtension(Purchase.Extension.HintMembershipRateInCents)?.valueInteger;
  }

  /** Returns true if this purchase is a recurring membership. */
  get hintMembershipIsRecurring(): boolean {
    return this.getExtension(Purchase.Extension.HintMembershipIsRecurring)?.valueBoolean ?? false;
  }

  /** Returns true if this purchase is a Hint membership. */
  get isHintMembership(): boolean {
    const membershipCoding = this.resource.code?.coding?.find(
      (coding) => coding.system === Purchase.Coding.System.HintMemberships,
    );
    return !!membershipCoding;
  }

  //----------------------------------------------------------------------------------------------
  //                                         CREATE HELPERS
  //----------------------------------------------------------------------------------------------

  /**
   * Creates a ChargeItem for a purchase. Supports service-based purchases, Hint charge items,
   * or Hint memberships. Idempotency is delegated to caller if needed.
   */
  static async create(
    medplum: MedplumClient,
    data: PurchaseCreateRequest,
    options?: PurchaseCreateOptions,
  ): Promise<Purchase> {
    const {
      account,
      service,
      stripePrice,
      hintChargeItem,
      hintMembership,
      status,
      quantity,
      dueDate,
      priceInCents,
      identifier,
      hintSyncStatus,
    } = data;

    // Validate that at least one purchasable is provided
    if (!service && !stripePrice && !hintChargeItem && !hintMembership) {
      throw new Error(
        "Purchase.create: Either service, stripePrice, hintChargeItem, or hintMembership must be provided",
      );
    }

    const accountId = resolveId(account);
    if (!accountId) {
      throw new Error(`Purchase.create: accountId not found for accountRef ${account.reference}`);
    }
    const memberAccount = await MemberAccount.getById(medplum, accountId);
    const practice = memberAccount?.ownerPracticeRef;
    const member = memberAccount?.memberRef;

    if (!practice || !member) {
      throw new Error(
        `Purchase.create: account missing owner or subject (owner=${!!practice}, subject=${!!member})`,
      );
    }

    // If service is provided, validate practice alignment
    if (service) {
      const serviceId = resolveId(service);
      if (!serviceId) {
        throw new Error(`Purchase.create: serviceId not found for serviceRef ${service.reference}`);
      }

      const serviceObject = await getService(medplum, serviceId);
      const practiceForService = await serviceObject.getPractice(medplum);

      if (practice.reference !== practiceForService.referenceString) {
        throw new Error(
          `Purchase.create: service practice ${practiceForService.referenceString} does not match account owner ${practice.reference}`,
        );
      }
    }

    const fhirObject = Purchase.toFhirCreateObject({
      member,
      service,
      // Forwarded like every other purchasable. Left out, a bare Stripe price built no coding at
      // all, so the ChargeItem went to Medplum with no `code` and the create was rejected — the
      // one purchasable with no `HealthcareService` behind it to supply one.
      stripePrice,
      hintChargeItem,
      hintMembership,
      account,
      practice,
      status,
      quantity,
      dueDate,
      priceInCents,
      identifier,
      hintSyncStatus,
    });
    if (options?.ifNoneExist) {
      const created = await Purchase.createResourceIfNoneExist(medplum, {
        resource: fhirObject,
        ifNoneExistQuery: options.ifNoneExist,
      });
      return new Purchase(created);
    }

    const created = await Purchase.createResource(medplum, fhirObject);
    return new Purchase(created);
  }

  /**
   * Creates a service-based ChargeItem using explicit references. Used when the caller already has
   * the account, patient, and practice references (e.g., webhook fallbacks).
   */
  static async createServicePurchaseWithExplicitRefs({
    medplum,
    account,
    patient,
    practice,
    serviceId,
    serviceName,
    status,
    quantity = 1,
    dueDate = time().add(30, "days").toISOString(),
  }: {
    medplum: MedplumClient;
    account: Reference<Account>;
    patient: Reference<Patient>;
    practice: Reference<Organization>;
    serviceId: string;
    serviceName?: string;
    status: PurchaseStatus;
    quantity?: number;
    dueDate?: string;
  }): Promise<Purchase> {
    const resource = await Purchase.createResource(medplum, {
      subject: patient,
      account: [account],
      performingOrganization: practice,
      status,
      quantity: { value: quantity },
      occurrenceDateTime: dueDate,
      code: {
        coding: [
          {
            system: Purchase.Coding.System.Services,
            code: serviceId,
            display: serviceName,
          },
        ],
      },
    });

    return new Purchase(resource);
  }

  //----------------------------------------------------------------------------------------------
  //                                         READ HELPERS
  //----------------------------------------------------------------------------------------------

  static async getById(medplum: MedplumClient, id: string): Promise<Purchase | undefined> {
    const resource = await Purchase.searchOneResource(medplum, { _id: id });
    return resource ? new Purchase(resource) : undefined;
  }

  //----------------------------------------------------------------------------------------------
  //                                         SEARCH HELPERS
  //----------------------------------------------------------------------------------------------

  static async searchOne(
    medplum: MedplumClient,
    query: PurchaseSearchQuery,
  ): Promise<Purchase | undefined> {
    const chargeItem = await Purchase.searchOneResource(medplum, this.toFhirQuery(query));
    return chargeItem ? new Purchase(chargeItem) : undefined;
  }

  static async search(medplum: MedplumClient, query: PurchaseSearchQuery): Promise<Purchase[]> {
    const results = await Purchase.searchResources(medplum, this.toFhirQuery(query));
    return results.map((result) => new Purchase(result));
  }

  static async findOneByBookingChargeKey(
    medplum: MedplumClient,
    {
      accountId,
      bookingChargeKey,
    }: {
      accountId: string;
      bookingChargeKey: string;
    },
  ): Promise<Purchase | undefined> {
    const resource = await Purchase.searchOneResource(medplum, {
      account: `Account/${accountId}`,
      identifier: `${Purchase.Identifier.BookingChargeKey}|${bookingChargeKey}`,
    });

    return resource ? new Purchase(resource) : undefined;
  }

  /**
   * Finds every purchase charged against one appointment — a visit can have more than one
   * attendee, each with their own Hint charge. Used to reverse every mapped Hint charge when an
   * appointment is cancelled or no-showed.
   */
  static async findAllByAppointmentId(
    medplum: MedplumClient,
    appointmentId: string,
  ): Promise<Purchase[]> {
    const resources = await Purchase.searchResources(
      medplum,
      { identifier: `${Purchase.Identifier.AppointmentId}|${appointmentId}` },
      // A visit realistically has a handful of Hint charges — one per attendee times charge item
      // — so this is a generous, deliberate bound, not a silent truncation (an unbounded search
      // returns one page, not the whole set — see the "Search with no bound" rule in AGENTS.md).
      { limit: 100 },
    );
    return resources.map((resource) => new Purchase(resource));
  }

  /**
   * Searches for Hint-related purchases (charges) associated with an account.
   * Uses Medplum-side filtering to only return purchases with Hint charge item coding.
   */
  static async searchHintCharges(
    medplum: MedplumClient,
    account: ResourceRef,
    options?: SearchOptions,
  ): Promise<{
    charges: Purchase[];
    totalCount: number;
  }> {
    const results = await Purchase.searchResources(
      medplum,
      this.toFhirQuery({
        account,
        isHintCharge: true,
      }),
      options,
    );

    const totalCount = await Purchase.getCount(
      medplum,
      this.toFhirQuery({
        account,
        isHintCharge: true,
      }),
      options,
    );

    return {
      charges: results.map((result) => new Purchase(result)),
      totalCount,
    };
  }

  /**
   * Searches for Hint-related purchases (memberships) associated with an account.
   * Uses Medplum-side filtering to only return purchases with Hint membership coding.
   * Can return multiple memberships per patient.
   */
  static async searchHintMemberships(
    medplum: MedplumClient,
    account: ResourceRef,
    options?: SearchOptions,
  ): Promise<{
    memberships: Purchase[];
    totalCount: number;
  }> {
    const results = await Purchase.searchResources(
      medplum,
      this.toFhirQuery({
        account,
        isHintMembership: true,
      }),
      options,
    );

    const totalCount = await Purchase.getCount(
      medplum,
      this.toFhirQuery({
        account,
        isHintMembership: true,
      }),
      options,
    );

    return {
      memberships: results.map((result) => new Purchase(result)),
      totalCount,
    };
  }

  /**
   * Counts Hint membership purchases associated with an account.
   * Uses Medplum-side filtering to only count purchases with Hint membership coding.
   */
  static async countHintMemberships(medplum: MedplumClient, account: ResourceRef): Promise<number> {
    return Purchase.getCount(
      medplum,
      this.toFhirQuery({
        account,
        isHintMembership: true,
      }),
    );
  }

  //----------------------------------------------------------------------------------------------
  //                                         UPDATE HELPERS
  //----------------------------------------------------------------------------------------------

  static async update(
    medplum: MedplumClient,
    id: string,
    data: PurchaseUpdateRequest,
  ): Promise<Purchase> {
    const resource = await Purchase.patchResource(medplum, {
      id,
      patch: this.toFhirUpdateObject(data),
    });
    return new Purchase(resource);
  }

  /** Marks a purchase as paid. */
  async markPaid(medplum: MedplumClient): Promise<Purchase> {
    return Purchase.update(medplum, this.id, { status: Purchase.Status.Paid });
  }

  /** Marks a purchase no longer billable — used once its mapped Hint charge is voided/reversed,
   * so a reconciliation view stops treating a dead charge as still owed or already collected. */
  async markVoided(medplum: MedplumClient): Promise<Purchase> {
    return Purchase.update(medplum, this.id, { status: Purchase.Status.NotBillable });
  }

  //----------------------------------------------------------------------------------------------
  //                                           SUBSCRIPTIONS
  //----------------------------------------------------------------------------------------------

  static subscribe(
    medplum: MedplumClient,
    query: PurchaseSearchQuery,
    callback: SubscriptionCallback<Purchase>,
  ) {
    return this.subscribeToResource(medplum, this.toFhirQuery(query), (resource, metadata) =>
      callback(new Purchase(resource), metadata),
    );
  }

  //----------------------------------------------------------------------------------------------
  //                                         FHIR HELPERS
  //----------------------------------------------------------------------------------------------

  private static toFhirCreateObject(data: PurchaseData): ResourceData<ChargeItem> {
    // create flow
    const {
      member,
      service,
      stripePrice,
      hintChargeItem,
      hintMembership,
      account,
      practice,
      status = Purchase.Status.Unpaid,
      quantity = 1,
      dueDate = time().add(30, "days").toISOString(),
      priceInCents,
      hintChargeId,
      hintInvoiceId,
      hintSyncStatus,
      hintChargeCreatedAt,
      identifier,
    } = data;

    // Build coding array based on what's provided
    const coding: {
      system: string;
      code: string;
      display?: string;
    }[] = [];

    if (service) {
      const serviceId = resolveId(service);
      if (serviceId) {
        coding.push({
          system: this.Coding.System.Services,
          code: serviceId,
          display: service.display,
        });
      }
    }

    if (stripePrice) {
      coding.push({
        system: this.Coding.System.StripePrices,
        code: stripePrice.id,
        display: stripePrice.name,
      });
    }

    if (hintChargeItem) {
      coding.push({
        system: this.Coding.System.HintChargeItems,
        code: hintChargeItem.id,
        display: hintChargeItem.name,
      });
    }

    if (hintMembership) {
      coding.push({
        system: this.Coding.System.HintMemberships,
        code: hintMembership.id,
        display: hintMembership.name,
      });
    }

    // Build extensions array
    const extensions = [];
    if (typeof priceInCents === "number" && priceInCents >= 0) {
      extensions.push({
        url: Purchase.Extension.PriceInCents,
        valueInteger: priceInCents,
      });
    }

    if (typeof hintChargeId === "string") {
      extensions.push({
        url: Purchase.Extension.HintChargeId,
        valueString: hintChargeId,
      });
    }

    if (typeof hintInvoiceId === "string") {
      extensions.push({
        url: Purchase.Extension.HintInvoiceId,
        valueString: hintInvoiceId,
      });
    }
    if (typeof hintSyncStatus === "string") {
      extensions.push({
        url: Purchase.Extension.HintSyncStatus,
        valueString: hintSyncStatus,
      });
    }

    if (typeof hintChargeCreatedAt === "string") {
      extensions.push({
        url: Purchase.Extension.HintChargeCreatedAt,
        valueDateTime: hintChargeCreatedAt,
      });
    }
    return {
      subject: member,
      account: [account],
      performingOrganization: practice,
      status,
      quantity: { value: quantity },
      occurrenceDateTime: dueDate,
      code: {
        coding,
      },
      extension: extensions.length > 0 ? extensions : undefined,
      identifier,
    };
  }

  private static toFhirUpdateObject(
    data: PurchaseUpdateRequest,
  ): Partial<ResourceData<ChargeItem>> {
    const {
      status,
      quantity,
      dueDate,
      hintChargeId,
      hintInvoiceId,
      hintSyncStatus,
      hintChargeCreatedAt,
      hintMembershipId,
      hintMembershipPlan,
      hintMembershipPlanType,
      hintMembershipPayer,
      hintMembershipCompany,
      hintMembershipStartDate,
      hintMembershipEndDate,
      hintMembershipStatus,
      hintMembershipPeriodInMonths,
      hintMembershipRateInCents,
      hintMembershipIsRecurring,
      priceInCents,
    } = data;

    const extensions = [];

    if (hintChargeId !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintChargeId,
        valueString: hintChargeId,
      });
    }

    if (hintInvoiceId !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintInvoiceId,
        valueString: hintInvoiceId,
      });
    }

    if (hintSyncStatus !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintSyncStatus,
        valueString: hintSyncStatus,
      });
    }

    if (hintChargeCreatedAt !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintChargeCreatedAt,
        valueDateTime: hintChargeCreatedAt,
      });
    }

    if (hintMembershipId !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipId,
        valueString: hintMembershipId,
      });
    }

    if (hintMembershipPlan !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipPlan,
        valueString: hintMembershipPlan,
      });
    }

    if (hintMembershipPlanType !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipPlanType,
        valueString: hintMembershipPlanType,
      });
    }

    if (hintMembershipPayer !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipPayer,
        valueString: hintMembershipPayer,
      });
    }

    if (hintMembershipCompany !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipCompany,
        valueString: hintMembershipCompany,
      });
    }

    if (hintMembershipStartDate !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipStartDate,
        valueString: hintMembershipStartDate,
      });
    }

    // null reaches patchResource as a delete of the extension
    if (hintMembershipEndDate !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipEndDate,
        valueString: hintMembershipEndDate,
      });
    }

    if (hintMembershipStatus !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipStatus,
        valueString: hintMembershipStatus,
      });
    }

    if (hintMembershipPeriodInMonths !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipPeriodInMonths,
        valueInteger: hintMembershipPeriodInMonths,
      });
    }

    if (hintMembershipRateInCents !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipRateInCents,
        valueInteger: hintMembershipRateInCents,
      });
    }

    if (hintMembershipIsRecurring !== undefined) {
      extensions.push({
        url: Purchase.Extension.HintMembershipIsRecurring,
        valueBoolean: hintMembershipIsRecurring,
      });
    }

    if (priceInCents !== undefined && typeof priceInCents === "number" && priceInCents >= 0) {
      extensions.push({
        url: Purchase.Extension.PriceInCents,
        valueInteger: priceInCents,
      });
    }

    return {
      status,
      quantity: quantity ? { value: quantity } : undefined,
      occurrenceDateTime: dueDate,
      extension: extensions.length > 0 ? extensions : undefined,
    };
  }

  private static toFhirQuery(query: PurchaseSearchQuery) {
    // Validate that conflicting filters aren't provided simultaneously
    const hasService = !!query.service;
    const hasHintChargeItem = !!query.hintChargeItem;
    const isHintCharge = query.isHintCharge === true;
    const isHintMembership = query.isHintMembership === true;
    const hasHintMembershipId = !!query.hintMembershipId;

    if (
      hasService &&
      (hasHintChargeItem || isHintCharge || isHintMembership || hasHintMembershipId)
    ) {
      throw new Error(
        "Purchase.toFhirQuery: Cannot search by service and Hint filters simultaneously",
      );
    }

    if (hasHintChargeItem && (isHintCharge || isHintMembership || hasHintMembershipId)) {
      throw new Error(
        "Purchase.toFhirQuery: Cannot search by specific hintChargeItem and Hint filters simultaneously",
      );
    }

    if (isHintCharge && (isHintMembership || hasHintMembershipId)) {
      throw new Error(
        "Purchase.toFhirQuery: Cannot search by Hint charges and Hint memberships simultaneously",
      );
    }

    const serviceId = resolveId(query.service);
    const hintChargeItemId = query.hintChargeItem?.id;

    // Build code parameter based on which filter is provided
    let code: string | undefined;
    if (serviceId) {
      code = `${Purchase.Coding.System.Services}|${serviceId}`;
    } else if (query.stripePriceId) {
      code = `${Purchase.Coding.System.StripePrices}|${query.stripePriceId}`;
    } else if (hintChargeItemId) {
      code = `${Purchase.Coding.System.HintChargeItems}|${hintChargeItemId}`;
    } else if (isHintCharge) {
      // Search for any Hint charge item by system prefix
      code = `${Purchase.Coding.System.HintChargeItems}|`;
    } else if (isHintMembership || hasHintMembershipId) {
      // Search for Hint memberships by system prefix
      if (hasHintMembershipId) {
        code = `${Purchase.Coding.System.HintMemberships}|${query.hintMembershipId}`;
      } else {
        code = `${Purchase.Coding.System.HintMemberships}|`;
      }
    }

    return {
      account: query.account?.reference,
      code,
    };
  }
}
